// ExampleHand — lay tiles + a win context and see the full engine breakdown live:
// parseAgari -> detectYaku -> computeFu -> score. Edits to the hand, the win tile, the context, OR
// the RuleSet (via `revision`) re-run the pipeline (save→reflect). The hand is composed VISUALLY via
// the <TilePicker> (click to add/remove); the canonical ids are shown read-only for reference.
import { useEffect, useState } from 'react';
import type { TileId } from '@mj/protocol';
import { useStudioStore, useStudioApi } from '../useStore';
import { makeNameOf, makeResultName, makeYakumanName } from '../yakuName';
import { TilePicker } from '../render/TilePicker';
import { buildWinContext } from '../store';

const DEFAULT_HAND: TileId[] = [
  'm2', 'm3', 'm4', 'p2', 'p3', 'p4', 's5', 's6', 's7', 's3', 's4', 's5', 'p8', 'p8',
];

interface Breakdown {
  parses: number;
  fired: string[];
  yakuman: string[];
  han: number;
  fu: number;
  points: number;
  error?: string;
}

export function ExampleHand() {
  const api = useStudioApi();
  const revision = useStudioStore((s) => s.revision);
  const [tiles, setTiles] = useState<TileId[]>(DEFAULT_HAND);
  const [winBy, setWinBy] = useState<'ron' | 'tsumo'>('tsumo');
  const [menzen, setMenzen] = useState(true);
  const [isDealer, setIsDealer] = useState(false);
  const [doraCount, setDoraCount] = useState(0);
  const [out, setOut] = useState<Breakdown | null>(null);

  const winTile = tiles[tiles.length - 1];

  useEffect(() => {
    let alive = true;
    if (tiles.length < 13 || !winTile) {
      setOut(null);
      return;
    }
    const st = api.getState();
    const ctx = buildWinContext({ winBy, menzen, isDealer, doraCount, winner: 0 });
    void (async () => {
      const parses = await st.engine.call('parseAgari', { concealed: tiles, melds: [], winTile, winBy });
      if (!alive) return;
      if (parses.length === 0) {
        setOut({ parses: 0, fired: [], yakuman: [], han: 0, fu: 0, points: 0, error: 'no valid decomposition' });
        return;
      }
      const yr = await st.engine.call('detectYaku', { ph: parses[0]!, ctx, rs: st.ruleSet, catalog: st.catalog, tiles });
      const scored = await st.engine.call('score', { parses, ctx, rs: st.ruleSet, catalog: st.catalog, tiles, opts: { players: st.ruleSet.players } });
      if (!alive) return;
      const nameOf = makeNameOf(st.catalog);
      const resultName = makeResultName(st.catalog);
      const ymName = makeYakumanName(st.catalog);
      if ('error' in scored) {
        setOut({ parses: parses.length, fired: yr.yaku.map((y) => `${nameOf(y.id)}(${y.han})`), yakuman: yr.yakuman.map((y) => `${ymName(y.id, y.mult)}×${y.mult}`), han: 0, fu: 0, points: 0, error: scored.error });
        return;
      }
      setOut({
        parses: parses.length,
        fired: scored.yaku.map((y) => `${resultName(y.id, y.han)}(${y.han})`),
        yakuman: yr.yakuman.map((y) => `${ymName(y.id, y.mult)}×${y.mult}`),
        han: scored.han,
        fu: scored.fu,
        points: scored.points,
      });
    })();
    return () => {
      alive = false;
    };
  }, [api, revision, winBy, menzen, isDealer, doraCount, tiles, winTile]);

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
          Compose the hand (click to add; click a picked tile to remove — last tile = winning tile):
        </label>
        <TilePicker value={tiles} onChange={setTiles} max={14} size={30} />
      </div>

      <div style={{ marginBottom: 10, fontSize: 12, color: '#666' }}>
        <span style={{ fontFamily: 'monospace' }}>{tiles.join(' ') || '—'}</span>
        {winTile && (
          <div style={{ marginTop: 4 }}>
            win tile: <strong>{winTile}</strong> ({tiles.length} tiles)
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
        <label>
          <select value={winBy} onChange={(e) => setWinBy(e.target.value as 'ron' | 'tsumo')}>
            <option value="tsumo">tsumo</option>
            <option value="ron">ron</option>
          </select>
        </label>
        <label>
          <input type="checkbox" checked={menzen} onChange={(e) => setMenzen(e.target.checked)} /> menzen
        </label>
        <label>
          <input type="checkbox" checked={isDealer} onChange={(e) => setIsDealer(e.target.checked)} /> dealer
        </label>
        <label>
          dora <input type="number" value={doraCount} min={0} max={20} onChange={(e) => setDoraCount(Number(e.target.value))} style={{ width: 54 }} />
        </label>
      </div>

      <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 12, background: '#fbfbfb' }}>
        <strong>Breakdown</strong>
        {!out ? (
          <div style={{ color: '#999' }}>enter ≥13 valid tiles</div>
        ) : out.error ? (
          <div style={{ color: '#c0202a', marginTop: 6 }}>
            {out.error} (decompositions: {out.parses})
            {out.fired.length > 0 && <div>detected: {out.fired.join(', ')}</div>}
          </div>
        ) : (
          <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.7 }}>
            <div>decompositions: {out.parses}</div>
            {out.yakuman.length > 0 ? (
              <div>
                yakuman: <strong style={{ color: '#a33' }}>{out.yakuman.join(', ')}</strong>
              </div>
            ) : (
              <div>yaku: {out.fired.join(', ') || '—'}</div>
            )}
            <div>
              {out.yakuman.length > 0 ? 'yakuman' : `${out.han} han ${out.fu} fu`} →{' '}
              <strong>{out.points.toLocaleString()} points</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
